/**
 * Suggests a better country/language context, and never forces one.
 *
 * Shopify retired the Geolocation app; the supported replacement is the Ajax
 * browsing_context_suggestions endpoint, which returns a suggestion only when the
 * visitor's detected context differs from the one they are browsing in.
 */
class CmLocaleSuggestion extends HTMLElement {
  static STORAGE_KEY = 'cm-locale-suggest-dismissed';

  connectedCallback() {
    if (this.#dismissed()) return;
    this.#run();
  }

  #dismissed() {
    try {
      return localStorage.getItem(CmLocaleSuggestion.STORAGE_KEY) === '1';
    } catch (error) {
      // Private windows and blocked site data throw on access; treat as not dismissed.
      return false;
    }
  }

  #remember() {
    try {
      localStorage.setItem(CmLocaleSuggestion.STORAGE_KEY, '1');
    } catch (error) {
      /* nothing to do — the banner simply reappears next visit */
    }
  }

  async #run() {
    const shopify = window.Shopify || {};
    const root = shopify.routes?.root || '/';
    const country = shopify.country || '';
    const language = shopify.language || shopify.locale || '';

    const url =
      `${root}browsing_context_suggestions.json` +
      `?country[enabled]=true&country[exclude]=${encodeURIComponent(country)}` +
      `&language[enabled]=true&language[exclude]=${encodeURIComponent(language)}`;

    let parts;
    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const data = await response.json();
      parts = data?.suggestions?.[0]?.parts;
    } catch (error) {
      return;
    }
    if (!parts) return;

    const suggestedCountry = parts.country?.handle && parts.country.handle !== country ? parts.country : null;
    const suggestedLanguage = parts.language?.handle && parts.language.handle !== language ? parts.language : null;
    if (!suggestedCountry && !suggestedLanguage) return;

    this.#render(suggestedCountry, suggestedLanguage);
  }

  #render(suggestedCountry, suggestedLanguage) {
    const label = suggestedCountry?.name || suggestedLanguage?.name || '';

    this.querySelector('[data-heading-slot]').textContent = (this.dataset.heading || '').replace('[country]', label);
    this.querySelector('[data-body-slot]').textContent = this.dataset.body || '';
    this.querySelector('[data-dismiss-slot]').textContent = this.dataset.dismiss || '';
    this.querySelector('[data-close-button]').setAttribute('aria-label', this.dataset.close || '');

    const bits = [];
    if (suggestedCountry) bits.push(suggestedCountry.name);
    if (suggestedLanguage) bits.push(suggestedLanguage.name);
    this.querySelector('[data-confirm-slot]').textContent = `${this.dataset.confirm} · ${bits.join(' · ')}`;

    if (suggestedCountry) this.querySelector('[data-country]').value = suggestedCountry.handle;
    if (suggestedLanguage) this.querySelector('[data-language]').value = suggestedLanguage.handle;

    for (const node of this.querySelectorAll('[data-close-button], [data-dismiss-slot]')) {
      node.addEventListener('click', () => {
        this.#remember();
        this.hidden = true;
      });
    }

    // Accepting is itself a choice — don't ask again either way.
    this.querySelector('form')?.addEventListener('submit', () => this.#remember());

    this.hidden = false;
  }
}

if (!customElements.get('cm-locale-suggestion')) {
  customElements.define('cm-locale-suggestion', CmLocaleSuggestion);
}
